/**
 * Channel Detail Page
 * 
 * Detailed view of a single channel with:
 * - Channel controls (start/stop/delete)
 * - Prefix configuration
 * - Live chat log
 * - Send messages as bot
 * - Module panels: each enabled module's ChannelPanel, below the chat (R25)
 */

import { useCallback, useState, useEffect, useRef } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import api, { apiErrorMessage } from '@/api/client'
import { useWebSocket, type WSMessage } from '@/hooks/useWebSocket'
import { useToast } from '@/components/Toast'
import ChatterPanel from '@/components/ChatterPanel'
import { getModule } from '@/modules'
import {
  ArrowLeft,
  Play,
  Square,
  Trash2,
  Radio,
  AlertCircle,
  Loader2,
  Send,
  Settings,
  MessageSquare,
  Layers,
  Clock,
  Users,
} from 'lucide-react'
import clsx from 'clsx'

/**
 * Channel data from the API.
 */
interface Channel {
  id: number
  owner_id: number
  twitch_channel_id: string
  twitch_channel_name: string
  command_prefix: string
  bot_enabled: boolean
  bot_connected: boolean
  settings: Record<string, unknown>
  /**
   * The bot that ACTUALLY speaks here, resolved server-side. Not necessarily
   * the assigned ChannelBot: the channel owner's bot override substitutes for
   * the system bot without rewriting the assignment.
   */
  effective_bot_username: string | null
  effective_bot_is_override: boolean
  enabled_modules: string[]
}

/**
 * One typed piece of a chat message (R19a).
 *
 * Twitch parses every message into ordered fragments and tells us what each one
 * IS. PratBot discarded that until R19a and rendered `message` as flat text.
 *
 * THE SAFETY RULE THIS TYPE EXISTS TO ENFORCE. An <img> may only ever be built
 * from a fragment Twitch itself typed `emote`. Text fragments are rendered as
 * JSX text children, which React escapes -- so a message containing a <script>
 * tag renders as literal, inert characters. Nothing here is ever passed to
 * dangerouslySetInnerHTML, and no emote is ever matched out of the raw message
 * string: an emote regex over untrusted chat text is an XSS vector wearing an
 * emote costume.
 *
 * An unknown `type` falls through to the text branch in ChatBody below, so a
 * future Twitch fragment type arrives escaped rather than unhandled.
 */
interface ChatFragment {
  type: 'text' | 'emote' | 'cheermote' | 'mention'
  text: string
  emote?: { id: string; set_id: string | null; animated: boolean; url: string }
  cheermote?: { prefix: string; bits: number; tier: number }
  mention?: { user_id: string; user_login: string; user_display: string }
}

/**
 * How a line was removed by a moderator (R18a).
 *
 * `deleted` = one message (channel.chat.message_delete).
 * `user_cleared` = every message by one chatter, from a timeout OR a ban
 * (channel.chat.clear_user_messages).
 *
 * A room-wide clear has no marker because it does not tombstone -- those lines
 * are dropped outright, server-side and here.
 */
type RemovedAction = 'deleted' | 'user_cleared'

/**
 * How a channel-point redeem or a Bits Power-up decorated a message (R19c).
 *
 * Carried VERBATIM from Twitch's `message_type`, and NULL for an ordinary
 * message -- the server folds `text`, unrecognised values and missing values
 * all to null, so "was this decorated" is a single null check here.
 *
 * Only three of the five drive a visual treatment. `user_intro` is not a redeem
 * at all, and Twitch's own client gives `channel_points_sub_only` no emphasis --
 * inventing one would say something the viewer did not pay for. Both are typed
 * so a later unit can render them without a backend change.
 */
type MessageDecoration =
  | 'channel_points_highlighted'
  | 'channel_points_sub_only'
  | 'user_intro'
  | 'power_ups_message_effect'
  | 'power_ups_gigantified_emote'

/**
 * Chat message for display.
 */
interface ChatMessage {
  id: string
  /**
   * The Twitch message id, when we have one. Distinct from `id`, which is only
   * ever a React key and falls back to a random string.
   *
   * This is the DEDUP key: the same line arrives twice whenever a replayed
   * backlog races a live broadcast, and before R18a there was no way to tell
   * that the two were the same message. Null for system lines, which are local
   * and cannot arrive twice.
   */
  messageId: string | null
  /** Speaker's Twitch user id -- what a `user_cleared` tombstone matches on. */
  chatterId: string | null
  type: 'chat' | 'system' | 'bot'
  username: string
  message: string
  timestamp: Date
  /**
   * Typed message pieces (R19a). EMPTY is the normal fallback state, not an
   * error: system lines have none, and so does every line stored between R18d
   * shipping and R19a -- those rows are NULL in the database and cannot be
   * backfilled, because fragment offsets exist only in the live event payload.
   * An empty array means "render `message` as plain text".
   */
  fragments: ChatFragment[]
  /**
   * Decoration (R19c). Null on an ordinary line, on a system line, and on every
   * line stored before R19c -- those rows are NULL in the database and cannot
   * be backfilled, because `message_type` exists only in the live event payload.
   * Unlike the attribution fields below, null here NEVER means "the broadcaster
   * did not consent": this rides the chat subscription's existing scope and so
   * is equally present on every channel.
   */
  messageType: MessageDecoration | null
  /**
   * The custom reward redeemed alongside this message. The ID only -- resolving
   * it to a title needs a scope R19c declined, so the UI shows a neutral marker
   * and puts the id in the tooltip rather than inventing a name.
   */
  channelPointsRewardId: string | null
  /** The chosen "animate my message" animation. An opaque id; Twitch publishes
   * no catalogue, so this drives the generic treatment rather than a named one. */
  channelPointsAnimationId: string | null
  removedAt: Date | null
  removedAction: RemovedAction | null
  /**
   * Moderator attribution on a tombstone (R18e). All four are null until a
   * `chat.attributed` frame arrives, and stay null forever on a channel whose
   * broadcaster never re-consented to `channel:moderate` -- that is the
   * intended silent degradation, not a bug. They are never a precondition of
   * `removedAt` / `removedAction`, which come from the unscoped event.
   */
  removedBy: string | null
  removedReason: string | null
  removedPermanent: boolean | null
  removedDurationSeconds: number | null
}

/**
 * IANA zone names for the timezone picker (R18c).
 *
 * Taken from `Intl.supportedValuesOf` rather than a hardcoded list, so the
 * options track whatever tz database the browser ships and cannot drift from
 * it. The server validates independently against Python's `zoneinfo`, which is
 * the authority — this list only decides what is easy to pick.
 *
 * The fallback is a short list rather than an empty one: if a browser lacks the
 * API, an empty select would make the field impossible to set, and "Not set"
 * would be the only reachable value.
 */
const TIMEZONE_NAMES: string[] = (() => {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const supported = (Intl as any)?.supportedValuesOf
    if (typeof supported === 'function') {
      const zones = supported.call(Intl, 'timeZone')
      if (Array.isArray(zones) && zones.length > 0) return zones as string[]
    }
  } catch {
    // Fall through to the static list below.
  }
  return [
    'UTC',
    'Australia/Adelaide',
    'Australia/Brisbane',
    'Australia/Melbourne',
    'Australia/Perth',
    'Australia/Sydney',
    'Europe/London',
    'America/New_York',
    'America/Los_Angeles',
  ]
})()

/**
 * Build a locally-generated system line (connect notice, bot started/stopped).
 */
function systemMessage(id: string, message: string): ChatMessage {
  return {
    id,
    messageId: null,
    chatterId: null,
    type: 'system',
    username: '',
    message,
    timestamp: new Date(),
    fragments: [],
    messageType: null,
    channelPointsRewardId: null,
    channelPointsAnimationId: null,
    removedAt: null,
    removedAction: null,
    removedBy: null,
    removedReason: null,
    removedPermanent: null,
    removedDurationSeconds: null,
  }
}

/**
 * Convert a wire chat entry into a display message.
 *
 * Handles both shapes, because they are the same shape: a live `chat` frame's
 * payload and a `chat.backlog` entry carry identical fields. The only
 * difference is that a backlog entry has its ORIGINAL timestamp baked in, while
 * a live one takes it from the envelope.
 */
function toChatMessage(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  entry: Record<string, any>,
  envelopeTimestamp?: string,
): ChatMessage {
  const messageId =
    typeof entry.message_id === 'string' && entry.message_id ? entry.message_id : null

  return {
    // Prefer the Twitch id so a replayed line and its live twin collapse to one
    // React row. The random fallback is what the whole log used before R18a.
    id: messageId ?? `${Date.now()}-${Math.random()}`,
    messageId,
    chatterId:
      typeof entry.chatter_id === 'string' && entry.chatter_id ? entry.chatter_id : null,
    type: entry.is_bot ? 'bot' : 'chat',
    username: entry.username || 'unknown',
    message: entry.message || '',
    timestamp: new Date(entry.timestamp || envelopeTimestamp || Date.now()),
    // Present on both live `chat` frames and replayed `chat.backlog` entries --
    // the round trip through chat_store is what makes a warmed line render the
    // same as a live one. Anything that is not an array becomes [], so a
    // malformed frame degrades to plain text rather than throwing in render.
    fragments: Array.isArray(entry.fragments) ? (entry.fragments as ChatFragment[]) : [],
    // Decoration (R19c). Present on both live `chat` frames and replayed
    // `chat.backlog` entries -- the round trip through chat_store is what stops
    // a warmed line rendering plain while its live twin renders highlighted, a
    // bug that would only appear after a RESTART. Anything that is not a string
    // becomes null, which is the undecorated case, so a malformed frame renders
    // an ordinary line rather than throwing.
    messageType:
      typeof entry.message_type === 'string' && entry.message_type
        ? (entry.message_type as MessageDecoration)
        : null,
    channelPointsRewardId:
      typeof entry.channel_points_reward_id === 'string' && entry.channel_points_reward_id
        ? entry.channel_points_reward_id
        : null,
    channelPointsAnimationId:
      typeof entry.channel_points_animation_id === 'string' &&
      entry.channel_points_animation_id
        ? entry.channel_points_animation_id
        : null,
    removedAt: entry.removed_at ? new Date(entry.removed_at) : null,
    removedAction: (entry.removed_action as RemovedAction | null) ?? null,
    // Present on replayed backlog entries, absent on live `chat` frames (which
    // predate any moderation by definition). Attribution rides the buffer entry
    // rather than the frame alone, so a reload shows the actor and not just the
    // strike-through -- R18e Fork 3 A.
    removedBy: entry.removed_by ?? null,
    removedReason: entry.removed_reason ?? null,
    removedPermanent: entry.removed_permanent ?? null,
    removedDurationSeconds: entry.removed_duration_seconds ?? null,
  }
}

/**
 * Render a timeout length for the tombstone tag.
 *
 * The server computes the number of seconds at event time; this only formats
 * it. Deliberately not derived from an end timestamp here -- the log survives
 * reloads, so a client-side subtraction would print a negative duration on a
 * line looked at five minutes later.
 */
function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`
  if (seconds < 86400) return `${Math.round(seconds / 3600)}h`
  return `${Math.round(seconds / 86400)}d`
}

/**
 * The parenthesised tag on a moderated line.
 *
 * Falls back to R18a's unattributed labels whenever attribution is absent, which
 * is the whole degradation story in one function: a channel whose broadcaster
 * has not re-consented never receives `core.chat.user_banned`, so `removedBy`
 * stays null and the line reads exactly as it did before R18e. The line is still
 * struck either way -- that is driven by the unscoped event and never by this.
 */
function removedLabelFor(msg: ChatMessage): string {
  if (msg.removedAction === 'deleted') return 'message deleted'
  if (!msg.removedBy) return 'messages cleared'

  const action = msg.removedPermanent
    ? `banned by ${msg.removedBy}`
    : msg.removedDurationSeconds
      ? `timed out ${formatDuration(msg.removedDurationSeconds)} by ${msg.removedBy}`
      : `timed out by ${msg.removedBy}`

  return msg.removedReason ? `${action} — ${msg.removedReason}` : action
}

/**
 * Is this a gigantified message (R19c)?
 *
 * Read in TWO places -- ChatBody sizes the emote, ChatMessageItem drops the
 * body onto its own line -- so it is a named predicate rather than the literal
 * repeated, which is how the two halves of a layout drift apart.
 */
function isGigantified(msg: ChatMessage): boolean {
  return msg.messageType === 'power_ups_gigantified_emote'
}

/**
 * Render a message body from its fragments, falling back to plain text.
 *
 * Every branch below produces either a JSX text child (escaped by React) or an
 * <img> whose src came from a fragment Twitch typed `emote`. There is no third
 * kind of output, and the `default` branch is what makes an unrecognised type
 * safe rather than unhandled.
 *
 * A cheermote renders as a TIER-STYLED BADGE rather than an image: TwitchIO
 * 3.2.2 gives us prefix / bits / tier and no artwork, and the images live behind
 * the Get Cheermotes API -- a fetch plus a cache plus an invalidation story,
 * which is a unit of its own. The badge satisfies "distinct from an emote"
 * without a new external dependency.
 *
 * GIGANTIFY IS A MESSAGE PROPERTY, NOT A FRAGMENT ONE (R19c). Twitch marks the
 * message and never says WHICH emote was gigantified -- there is no per-fragment
 * power-up flag on channel.chat.message, and the emote identity lives only on
 * the Bits event R19c declined to subscribe to. So every emote in a gigantified
 * message scales. In practice such a message is emote-only and the two agree;
 * a multi-emote one scales all of them, which is visibly plausible rather than
 * broken.
 */
function ChatBody({ msg }: { msg: ChatMessage }) {
  // Twitch renders a gigantified emote BENEATH the username on its own line and
  // at roughly four times a normal emote. Matching that is not cosmetic: inline
  // at h-12 it read as "a slightly large emote", which is the one thing a
  // gigantify must not look like, since the viewer paid for it to be
  // unmissable. `block` is what drops it to its own line -- size alone would
  // only have stretched the line box and pushed the username off-centre.
  const emoteClass = isGigantified(msg)
    ? 'block h-24 w-auto my-1'
    : 'inline-block align-middle h-6 w-auto mx-0.5'

  if (!msg.fragments.length) {
    return <>{msg.message}</>
  }

  return (
    <>
      {msg.fragments.map((frag, i) => {
        switch (frag.type) {
          case 'emote':
            if (!frag.emote) return <span key={i}>{frag.text}</span>
            return (
              <img
                key={i}
                src={frag.emote.url}
                alt={frag.text}
                title={frag.text}
                loading="lazy"
                className={emoteClass}
              />
            )
          case 'cheermote':
            if (!frag.cheermote) return <span key={i}>{frag.text}</span>
            return (
              <span
                key={i}
                title={`${frag.cheermote.bits} bits (tier ${frag.cheermote.tier})`}
                className="inline-block align-middle mx-0.5 px-1.5 py-0.5 rounded text-xs font-semibold bg-synth-cyan/20 text-synth-cyan border border-synth-cyan/40"
              >
                {frag.cheermote.prefix}
                {frag.cheermote.bits}
              </span>
            )
          case 'mention':
            return (
              <span key={i} className="text-synth-mint font-medium">
                {frag.text}
              </span>
            )
          default:
            // `text`, and anything unrecognised. A JSX text child -- React
            // escapes it, so markup in chat renders as inert characters.
            return <span key={i}>{frag.text}</span>
        }
      })}
    </>
  )
}

/**
 * Twitch's slug shape for a Power-up value, e.g. `rainbow-eclipse`, `simmer`.
 *
 * A GUARD, not a parser. Two real samples say message effects arrive as
 * readable slugs, but nobody has ever redeemed an Animate My Message, and that
 * path writes to the SAME column -- it may well put an opaque id there. So a
 * value earns a humanised label only by looking like a slug, and anything else
 * falls back to the generic wording rather than printing a UUID at a reader.
 * The generic path stays live on purpose: deleting it on the strength of two
 * rows is how the original wrong claim got made in the first place.
 */
const POWER_UP_SLUG = /^[a-z0-9]+(?:-[a-z0-9]+)*$/
const UUID_SHAPE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/**
 * `rainbow-eclipse` -> `Rainbow Eclipse`, or null if it does not look like a
 * slug. Null is the caller's cue to use the generic label.
 */
function powerUpName(raw: string | null): string | null {
  if (!raw) return null
  // A UUID satisfies the slug pattern (lowercase hex plus hyphens), so it is
  // excluded by shape before the length test rather than relying on it.
  if (UUID_SHAPE.test(raw)) return null
  if (raw.length > 32) return null
  if (!POWER_UP_SLUG.test(raw)) return null
  const words = raw.split('-')
  // PER-SEGMENT length, not just total. A 32-char unhyphenated hex blob sits
  // exactly at the total limit, uses only lowercase alphanumerics and contains
  // no hyphens -- so it cleared every other test and would have been printed
  // verbatim at a reader. Real effect names are words: `simmer`, `rainbow`,
  // `eclipse`. Nothing legitimate needs a fifteen-character segment.
  if (words.some((word) => word.length > 14)) return null
  return words
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

/**
 * Line treatment and label for a decorated message (R19c), or null.
 *
 * TWO VISUAL FAMILIES, and the split is BITS versus CHANNEL POINTS.
 *
 * PURPLE is channel points -- `channel_points_highlighted` is Twitch's own
 * highlight and gets Twitch's own colour, so it reads as the thing it is.
 *
 * GOLD is Bits: message effects and gigantified emotes, the things a viewer
 * spent currency on.
 *
 * THAT AXIS IS A RE-FOUNDING, AND THE HISTORY MATTERS. The colour originally
 * meant "paid to stand out and we CANNOT NAME IT", on the belief that the
 * message effect was unreadable here. Real rows disproved that -- the effect
 * name is in `channel_points_animation_id` and always was -- which left the
 * palette's stated reason false while the palette itself was still right. It
 * is re-founded on Bits-versus-points rather than repainted: every line renders
 * in the same place as before, and the justification is now true.
 *
 * `label` IS RENDERED AS A VISIBLE SUB-LINE AND REPLACES THE TOOLTIP THAT USED
 * TO CARRY IT. A tooltip is invisible until hovered, which was the same problem
 * class as a degradation nobody can see. `title` survives only where it carries
 * something the sub-line does NOT: the raw reward id, and a Power-up value the
 * shape guard rejected -- the latter precisely because an unhumanisable value
 * is the case somebody will need to debug.
 *
 * THE LABELS NAME THE REDEMPTION AND STOP THERE. No parenthetical explaining
 * what Twitch does or does not expose -- a reader of the log wants to know what
 * was redeemed, not the shape of an API.
 *
 * ONE DECORATION PER LINE, message_type winning over both ids. Twitch's model
 * makes the overlap unreachable in practice -- a highlight IS an automatic
 * reward, so it never arrives alongside a custom `channel_points_id` -- and a
 * line that stacked two sub-lines would cost more than the case is worth.
 */
function decorationFor(
  msg: ChatMessage,
): { className: string | null; label: string; title?: string } | null {
  if (msg.messageType === 'channel_points_highlighted') {
    return {
      className: 'border-l-2 border-synth-purple bg-synth-purple/10',
      label: 'Highlighted with channel points',
    }
  }
  if (msg.messageType === 'power_ups_message_effect') {
    // The effect NAME, which R19c spent its first day believing it could not
    // have. Falls back to the generic wording when the guard rejects the value.
    const name = powerUpName(msg.channelPointsAnimationId)
    return {
      className: 'border-l-2 border-synth-gold bg-synth-gold/10',
      label: name ? `Bits Power-up — ${name}` : 'Bits Power-up — message effect',
      title: name ? undefined : msg.channelPointsAnimationId ?? undefined,
    }
  }
  if (msg.messageType === 'power_ups_gigantified_emote') {
    // No bar and no tint -- the emote's size already says a Power-up was spent,
    // and tinting as well would signal one purchase twice. The label still
    // earns its place: it separates "gigantified" from "someone posted a large
    // emote", which size alone cannot say.
    return { className: null, label: 'Bits Power-up — gigantified emote' }
  }
  // Checked AFTER message_type because an animated redemption can arrive on a
  // message whose type is ordinary -- the animation id is an independent signal,
  // not a variant of the enum. NOTE this branch has never been reached in the
  // wild: no Animate My Message redemption has been observed, so whether the
  // value here is a slug or something opaque is genuinely unknown, and the
  // guard is the whole reason that is safe to leave unresolved.
  if (msg.channelPointsAnimationId) {
    const name = powerUpName(msg.channelPointsAnimationId)
    return {
      className: 'border-l-2 border-synth-gold bg-synth-gold/10',
      label: name ? `Animated message — ${name}` : 'Animated message',
      title: name ? undefined : msg.channelPointsAnimationId,
    }
  }
  if (msg.channelPointsRewardId) {
    // No bar: this is context, not emphasis -- "a reward was redeemed with this
    // message" is not the viewer asking to be noticed. The id keeps a tooltip
    // because it is the one thing the sub-line does not say; resolving it to a
    // TITLE needs the scope R19c declined, and a raw id inline would be noise.
    return {
      className: null,
      label: 'Channel-point reward',
      title: `Reward ${msg.channelPointsRewardId}`,
    }
  }
  return null
}

/**
 * Chat message component.
 */
function ChatMessageItem({
  msg,
  onSelectChatter,
}: {
  msg: ChatMessage
  /**
   * Open the chatter panel for this speaker (R18c). OPTIONAL, and the name is
   * rendered as plain text when it is absent or when the line carries no
   * `chatterId` — system lines have none, and so does every line replayed from
   * before the id was captured. A name that looks clickable and does nothing is
   * worse than one that never looked clickable.
   */
  onSelectChatter?: (twitchUserId: string, name: string) => void
}) {
  const timeStr = msg.timestamp.toLocaleTimeString([], { 
    hour: '2-digit', 
    minute: '2-digit' 
  })

  // A moderated line is struck through and dimmed rather than removed, so the
  // log reads as a record of what happened in the channel (R18a Decision 2a).
  //
  // Since R18e the tag says WHO did it as well, WHERE THAT IS KNOWN. It is known
  // only on channels whose broadcaster re-consented to `channel:moderate`, since
  // only channel.ban carries a moderator; elsewhere removedLabelFor() falls back
  // to R18a's wording. What never varies is whether the line is struck at all --
  // that rides the unscoped clear_user_messages on every channel.
  const removed = msg.removedAction !== null
  const removedLabel = removedLabelFor(msg)
  const decoration = decorationFor(msg)
  
  return (
    <div
      className={clsx(
        'py-1.5 px-2 rounded',
        msg.type === 'system' && 'bg-surface-raised/50 text-text-secondary text-sm italic',
        // Mutually exclusive with the decoration below rather than layered:
        // both set border-l colour at the same specificity, so letting both
        // through would leave which one wins to stylesheet order. Tested on
        // `decoration?.className` and NOT on `decoration`, because the
        // gigantify and custom-reward cases return a decoration whose className
        // is null -- they carry a label but no bar, and must not suppress the
        // bot styling they do not replace.
        msg.type === 'bot' && !decoration?.className && 'bg-synth-magenta/10 border-l-2 border-synth-magenta',
        decoration?.className,
        removed && 'opacity-60'
      )}
    >
      <span className="text-text-muted text-xs mr-2">{timeStr}</span>
      {msg.type !== 'system' && (
        <span
          className={clsx(
            'font-medium mr-1',
            removed && 'line-through',
            msg.type === 'bot' ? 'text-synth-magenta' : 'text-state-info'
          )}
        >
          {onSelectChatter && msg.chatterId ? (
            <button
              type="button"
              onClick={() => onSelectChatter(msg.chatterId!, msg.username)}
              className="hover:underline focus:underline focus:outline-none"
              title={`Show ${msg.username}'s activity`}
            >
              {msg.username}
            </button>
          ) : (
            msg.username
          )}
          :
        </span>
      )}
      <span
        className={clsx(
          msg.type === 'system' ? '' : 'text-text-secondary',
          // R19c. Block so the gigantified emote sits UNDER the username the
          // way Twitch's own chat renders it, rather than inline beside it.
          // `ml-10` stops it starting in the TIMESTAMP GUTTER: it is the same
          // offset the decoration sub-line uses, so the emote, the message and
          // the label all line up under the username as one column.
          isGigantified(msg) && 'block ml-10',
          removed && 'line-through'
        )}
      >
        <ChatBody msg={msg} />
      </span>
      {removed && (
        <span className="ml-2 text-text-muted text-xs italic no-underline">
          ({removedLabel})
        </span>
      )}
      {decoration && (
        // Placed AFTER the removal tag so attribution stays inline with the
        // message it belongs to, rather than being pushed below a sub-line that
        // describes something else entirely.
        //
        // `ml-10` lines the label up under the username rather than the
        // timestamp -- it is describing the message, not the clock. That is a
        // measured offset against a text-xs timestamp plus its mr-2, so it is
        // worth re-checking if either changes.
        <div className="ml-10 text-xs text-text-muted" title={decoration.title}>
          {decoration.label}
        </div>
      )}
    </div>
  )
}

/**
 * Channel Detail page component.
 */
export default function ChannelDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { isConnected, subscribe, unsubscribe, addMessageHandler } = useWebSocket()
  
  const [channel, setChannel] = useState<Channel | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [isActionLoading, setIsActionLoading] = useState(false)
  
  // Chat state
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([])
  const [messageInput, setMessageInput] = useState('')
  const [isSending, setIsSending] = useState(false)
  const chatContainerRef = useRef<HTMLDivElement>(null)
  
  // Prefix editing
  const [isEditingPrefix, setIsEditingPrefix] = useState(false)
  const [newPrefix, setNewPrefix] = useState('')

  // Timezone editing (R18c). The value lives in `channel.settings.timezone`;
  // it is written through its own endpoint rather than the generic channel
  // PATCH, which replaces the whole settings blob wholesale.
  const [isEditingTimezone, setIsEditingTimezone] = useState(false)
  const [newTimezone, setNewTimezone] = useState('')

  // The chatter whose panel is open, or null. Identified by Twitch user id,
  // which every non-system chat line already carries.
  const [selectedChatter, setSelectedChatter] =
    useState<{ id: string; name: string } | null>(null)

  const { success, error } = useToast()

  /**
   * Every channel write goes through here: busy flag, then toast.
   *
   * `call` returns the server's own message where it sends one (start/stop name
   * the channel), and nothing where the outcome is fully known here.
   */
  const mutate = useCallback(
    async (
      call: () => Promise<string | void>,
      successMessage: string,
      failureMessage: string,
    ) => {
      setIsActionLoading(true)
      try {
        const detail = await call()
        success(typeof detail === 'string' && detail ? detail : successMessage)
      } catch (err) {
        error(apiErrorMessage(err, failureMessage))
      } finally {
        setIsActionLoading(false)
      }
    },
    [success, error],
  )
  
  // Fetch channel data
  useEffect(() => {
    async function fetchChannel() {
      if (!id) return
      
      try {
        const data = await api.get<Channel>(`/api/channels/${id}`)
        setChannel(data)
        setNewPrefix(data.command_prefix)
        setLoadError(null)
        
        // Add system message. Scrollback replayed by the server lands ABOVE
        // this line (see the chat.backlog handler), which turns it into a
        // "you joined here" marker between the backlog and the live feed.
        setChatMessages([
          systemMessage('init', `Connected to #${data.twitch_channel_name} chat log`),
        ])
      } catch (err) {
        // Fatal load failure stays INLINE, not a toast: a page that cannot
        // render has nothing to toast over.
        setLoadError(apiErrorMessage(err, 'Failed to load channel'))
      } finally {
        setIsLoading(false)
      }
    }
    
    fetchChannel()
  }, [id])
  
  // Handle WebSocket messages for chat
  useEffect(() => {
    if (!channel) return
    
    const channelId = parseInt(id || '0')

    const removeHandler = addMessageHandler((wsMessage: WSMessage) => {
      // Handle chat messages for this channel
      if (wsMessage.type === 'chat' && wsMessage.payload?.channel_id === channelId) {
        const newMsg = toChatMessage(wsMessage.payload, wsMessage.timestamp)
        setChatMessages(prev => {
          // A live line can arrive between the server authorising our subscribe
          // and the backlog frame being sent, so the same message can reach us
          // twice. Drop the duplicate rather than rendering it twice.
          if (newMsg.messageId && prev.some(m => m.messageId === newMsg.messageId)) {
            return prev
          }
          return [...prev, newMsg]
        })
      }

      // Scrollback replay, sent once per subscribe (R18a).
      if (
        wsMessage.type === 'chat.backlog' &&
        wsMessage.payload?.channel_id === channelId
      ) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const entries: Record<string, any>[] = wsMessage.payload.messages || []
        setChatMessages(prev => {
          const seen = new Set(
            prev.map(m => m.messageId).filter((v): v is string => v !== null)
          )
          const backlog = entries
            .map(entry => toChatMessage(entry))
            .filter(m => !m.messageId || !seen.has(m.messageId))

          if (backlog.length === 0) return prev
          // PREPENDED, not appended: every backlog line is older than anything
          // already in state, so this is the only placement that keeps the log
          // in order when a live message beat the replay frame here.
          return [...backlog, ...prev]
        })
      }

      // A moderator removed something. Mark the affected lines in place rather
      // than dropping them -- same bargain as the server-side buffer.
      if (
        wsMessage.type === 'chat.moderated' &&
        wsMessage.payload?.channel_id === channelId
      ) {
        const { action, message_id: modMessageId, chatter_id: modChatterId, removed_at } =
          wsMessage.payload
        const removedAt = new Date(removed_at || Date.now())
        setChatMessages(prev =>
          prev.map(m => {
            if (m.removedAt) return m
            const hit =
              action === 'deleted'
                ? Boolean(modMessageId) && m.messageId === modMessageId
                : Boolean(modChatterId) && m.chatterId === modChatterId
            return hit
              ? { ...m, removedAt, removedAction: action as RemovedAction }
              : m
          })
        )
      }

      // A ban or timeout named the moderator behind lines that are ALREADY
      // struck (R18e). Enrichment only: this never sets removedAt and never
      // appends, so a frame naming lines this tab has not seen -- evicted past
      // the 200 cap, or a tab that connected after the fact -- matches nothing
      // and is a silent no-op rather than an error or a synthesised row.
      if (
        wsMessage.type === 'chat.attributed' &&
        wsMessage.payload?.channel_id === channelId
      ) {
        const {
          chatter_id: attrChatterId,
          since,
          removed_by,
          removed_reason,
          removed_permanent,
          removed_duration_seconds,
        } = wsMessage.payload
        // The same correlation window the server just applied, shipped on the
        // frame so the two cannot drift apart.
        const sinceDate = new Date(since)
        setChatMessages(prev =>
          prev.map(m => {
            if (m.removedAction !== 'user_cleared') return m
            if (m.removedBy) return m
            if (!m.removedAt || m.removedAt < sinceDate) return m
            if (!attrChatterId || m.chatterId !== attrChatterId) return m
            return {
              ...m,
              removedBy: removed_by ?? null,
              removedReason: removed_reason ?? null,
              removedPermanent: removed_permanent ?? null,
              removedDurationSeconds: removed_duration_seconds ?? null,
            }
          })
        )
      }

      // The whole room was cleared. Drop chat outright -- this is the panic
      // button, and struck-through text is still text on screen.
      if (
        wsMessage.type === 'chat.cleared' &&
        wsMessage.payload?.channel_id === channelId
      ) {
        setChatMessages(prev => [
          ...prev.filter(m => m.type === 'system'),
          systemMessage(`clear-${Date.now()}`, 'Chat was cleared by a moderator'),
        ])
      }
      
      // Handle bot status updates
      if (wsMessage.type === 'channel_status' && wsMessage.channel_id === channelId) {
        setChannel(prev => prev ? {
          ...prev,
          bot_connected: wsMessage.payload.connected,
          bot_enabled: wsMessage.payload.enabled,
        } : null)
      }
    })
    
    return removeHandler
  }, [channel, id, addMessageHandler])
  
  // Subscribe to this channel's events.
  //
  // Must wait for the socket to actually be OPEN — send() silently drops
  // anything issued before then, so subscribing on mount alone would be a
  // no-op on a cold load. Server-side the subscription is authorised against
  // the user's channel access, so naming an id you can't see grants nothing.
  //
  // ALSO waits for the channel fetch, which is what `channelLoaded` is for.
  // The server replies to subscribe with a chat.backlog frame, and the message
  // handler above is itself gated on `channel` — so on a cold load where the
  // socket opens before GET /api/channels/{id} resolves, subscribing first
  // means the backlog arrives with nothing listening and is dropped on the
  // floor. The handler effect is declared ABOVE this one, so when both become
  // ready in the same commit React runs it first and the listener is in place.
  //
  // A derived boolean rather than `channel` itself: the object identity changes
  // on every channel_status update, which would churn unsubscribe/subscribe —
  // and, now, re-replay the backlog — on each one.
  const channelLoaded = channel !== null
  useEffect(() => {
    if (!isConnected || !id || !channelLoaded) return
    const channelId = parseInt(id, 10)
    if (Number.isNaN(channelId)) return

    subscribe([channelId])
    return () => unsubscribe([channelId])
  }, [isConnected, id, channelLoaded, subscribe, unsubscribe])

  // Auto-scroll chat
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight
    }
  }, [chatMessages])
  
  // Send message as bot
  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!messageInput.trim() || !channel || isSending) return
    
    setIsSending(true)
    
    try {
      await api.post(`/api/channels/${id}/message`, {
        message: messageInput.trim(),
      })
      
      // No optimistic append — the message arrives back through the chat log
      // via EventSub like any other, so appending here would show it twice.
      setMessageInput('')
    } catch (err) {
      // No success toast: the message arrives back through the chat log,
      // which is the report.
      error(apiErrorMessage(err, 'Failed to send message'))
    } finally {
      setIsSending(false)
    }
  }
  
  // Start bot
  const handleStart = async () => {
    if (!channel) return
    await mutate(
      async () => {
        const result = await api.post<{ message: string }>(`/api/channels/${id}/start`)
        setChannel(prev => prev ? { ...prev, bot_enabled: true, bot_connected: true } : null)
        
        // Add system message
        setChatMessages(prev => [
          ...prev,
          systemMessage(`sys-${Date.now()}`, 'Bot started'),
        ])
        return result.message
      },
      'Bot started',
      'Failed to start bot',
    )
  }
  
  // Stop bot
  const handleStop = async () => {
    if (!channel) return
    await mutate(
      async () => {
        const result = await api.post<{ message: string }>(`/api/channels/${id}/stop`)
        setChannel(prev => prev ? { ...prev, bot_enabled: false, bot_connected: false } : null)
        
        // Add system message
        setChatMessages(prev => [
          ...prev,
          systemMessage(`sys-${Date.now()}`, 'Bot stopped'),
        ])
        return result.message
      },
      'Bot stopped',
      'Failed to stop bot',
    )
  }
  
  // Delete channel
  const handleDelete = async () => {
    if (!channel) return
    
    if (!confirm(`Are you sure you want to remove #${channel.twitch_channel_name}? This cannot be undone.`)) {
      return
    }
    
    const name = channel.twitch_channel_name
    await mutate(
      async () => {
        await api.delete(`/api/channels/${id}`)
        // ToastProvider sits above the router in main.tsx, so the notification
        // outlives this navigation. The old 1s setTimeout existed only because
        // a page-local toast died with its page.
        navigate('/channels')
      },
      `Removed #${name}`,
      'Failed to remove channel',
    )
  }
  
  // Update prefix
  /**
   * Write (or clear) the channel timezone.
   *
   * Goes to a DEDICATED endpoint, not `PATCH /api/channels/{id}`. That handler
   * replaces `settings` wholesale, so sending a timezone through it would mean
   * this page had to round-trip the entire settings blob or silently destroy
   * every other key in it.
   *
   * Nothing stored changes shape as a result of this: the activity histogram
   * stays in UTC and is converted at read. Saving a timezone changes what the
   * chatter panel draws and nothing else.
   */
  const handleUpdateTimezone = async () => {
    const current = (channel?.settings?.timezone as string | undefined) ?? ''
    if (!channel || newTimezone === current) {
      setIsEditingTimezone(false)
      return
    }

    await mutate(
      async () => {
        const updated = await api.patch<Channel>(
          `/api/channels/${id}/timezone`,
          { timezone: newTimezone || null },
        )
        // Take the SERVER's settings blob rather than patching our copy: it is
        // the only thing that knows what else lives in there.
        setChannel(prev => (prev ? { ...prev, settings: updated.settings } : null))
        setIsEditingTimezone(false)
      },
      newTimezone ? `Timezone set to ${newTimezone}` : 'Timezone cleared',
      'Could not update the timezone',
    )
  }

  const handleUpdatePrefix = async () => {
    if (!channel || newPrefix === channel.command_prefix) {
      setIsEditingPrefix(false)
      return
    }
    
    await mutate(
      async () => {
        await api.patch(`/api/channels/${id}`, {
          command_prefix: newPrefix,
        })
        setChannel(prev => prev ? { ...prev, command_prefix: newPrefix } : null)
        setIsEditingPrefix(false)
      },
      'Prefix updated',
      'Failed to update prefix',
    )
  }
  
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-synth-magenta"></div>
      </div>
    )
  }
  
  if (loadError || !channel) {
    return (
      <div className="space-y-4">
        <Link to="/channels" className="inline-flex items-center gap-2 text-text-secondary hover:text-text-primary">
          <ArrowLeft size={18} />
          Back to Channels
        </Link>
        <div className="bg-state-danger-bg border border-state-danger rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="text-state-danger" size={20} />
          <span className="text-state-danger">{loadError || 'Channel not found'}</span>
        </div>
      </div>
    )
  }
  
  const status = channel.bot_connected ? 'connected' : channel.bot_enabled ? 'disconnected' : 'disabled'
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/channels" className="p-2 text-text-secondary hover:text-text-primary hover:bg-surface-overlay rounded-lg">
            <ArrowLeft size={20} />
          </Link>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-synth-magenta/20 flex items-center justify-center">
              <Radio className="text-synth-magenta" size={24} />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-text-primary">#{channel.twitch_channel_name}</h1>
              <div className="flex items-center gap-2 text-sm">
                <div
                  className={clsx(
                    'w-2 h-2 rounded-full',
                    status === 'connected' && 'bg-synth-mint',
                    status === 'disconnected' && 'bg-state-warning',
                    status === 'disabled' && 'bg-neutral-600'
                  )}
                />
                <span className={clsx(
                  status === 'connected' && 'text-synth-mint',
                  status === 'disconnected' && 'text-state-warning',
                  status === 'disabled' && 'text-text-muted'
                )}>
                  {status === 'connected' ? 'Connected' : status === 'disconnected' ? 'Disconnected' : 'Disabled'}
                </span>
              </div>
            </div>
          </div>
        </div>
        
        {/* Action buttons */}
        <div className="flex items-center gap-2">
          {/* R18f. The chat log below reaches a chatter only if they are
              talking right now; this reaches everyone who ever has. */}
          <Link
            to={`/channels/${channel.id}/chatters`}
            className="btn-secondary flex items-center gap-2"
            title="Browse chatters"
          >
            <Users size={16} />
            Chatters
          </Link>
          <Link
            to={`/channels/${channel.id}/overlays`}
            className="btn-secondary flex items-center gap-2"
            title="Manage overlays"
          >
            <Layers size={16} />
            Overlays
          </Link>
          {isActionLoading ? (
            <div className="p-2">
              <Loader2 className="animate-spin text-text-secondary" size={20} />
            </div>
          ) : channel.bot_connected ? (
            <button
              onClick={handleStop}
              className="btn-secondary flex items-center gap-2"
              disabled={isActionLoading}
            >
              <Square size={16} />
              Stop
            </button>
          ) : (
            <button
              onClick={handleStart}
              className="btn-primary flex items-center gap-2"
              disabled={isActionLoading}
            >
              <Play size={16} />
              Start
            </button>
          )}
          <button
            onClick={handleDelete}
            className="p-2 text-text-secondary hover:text-state-danger hover:bg-surface-overlay rounded-lg"
            title="Delete channel"
            disabled={isActionLoading}
          >
            <Trash2 size={20} />
          </button>
        </div>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Settings Panel */}
        <div className="card">
          <h2 className="text-lg font-semibold text-text-primary mb-4 flex items-center gap-2">
            <Settings size={20} />
            Settings
          </h2>
          
          <div className="space-y-4">
            {/* Bot identity — READ-ONLY here.
                Which account speaks is a USER-level choice (one bot replaces
                the system bot across every channel you own), so editing it
                per channel would be a lie. The prefix below stays editable
                because that genuinely is per-channel. */}
            <div>
              <label className="block text-sm text-text-secondary mb-2">Bot</label>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-text-primary">
                  {channel.effective_bot_username || 'Unknown'}
                </span>
                {channel.effective_bot_is_override && (
                  <span className="px-2 py-0.5 rounded text-xs bg-synth-magenta/15 text-synth-magenta">
                    Your bot
                  </span>
                )}
              </div>
              {channel.effective_bot_is_override ? (
                <p className="text-text-muted text-xs mt-1.5 leading-relaxed">
                  Mod it here (
                  <code className="font-mono">
                    /mod {channel.effective_bot_username}
                  </code>
                  ) or this channel's follow events stop.{' '}
                  <Link to="/settings/bot" className="text-synth-cyan hover:underline">
                    Change
                  </Link>
                </p>
              ) : (
                <p className="text-text-muted text-xs mt-1.5 leading-relaxed">
                  <Link to="/settings/bot" className="text-synth-cyan hover:underline">
                    Connect your own bot
                  </Link>{' '}
                  to replace it across the channels you own.
                </p>
              )}
            </div>

            {/* Command Prefix */}
            <div>
              <label className="block text-sm text-text-secondary mb-2">Command Prefix</label>
              {isEditingPrefix ? (
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newPrefix}
                    onChange={(e) => setNewPrefix(e.target.value.slice(0, 5))}
                    className="input w-20"
                    maxLength={5}
                    autoFocus
                  />
                  <button
                    onClick={handleUpdatePrefix}
                    className="btn-primary text-sm"
                    disabled={isActionLoading}
                  >
                    Save
                  </button>
                  <button
                    onClick={() => {
                      setNewPrefix(channel.command_prefix)
                      setIsEditingPrefix(false)
                    }}
                    className="btn-secondary text-sm"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <code className="bg-surface-overlay px-3 py-1.5 rounded text-text-primary">
                    {channel.command_prefix}
                  </code>
                  <button
                    onClick={() => setIsEditingPrefix(true)}
                    className="text-sm text-text-secondary hover:text-text-primary"
                  >
                    Edit
                  </button>
                </div>
              )}
            </div>
            
            {/* Timezone (R18c) — the read-side lens on R18b's histogram.
                Editing this changes only what the chatter panel DRAWS; stored
                buckets are UTC and are converted on every read. */}
            <div>
              <label className="block text-sm text-text-secondary mb-2 flex items-center gap-1.5">
                <Clock size={14} />
                Timezone
              </label>
              {isEditingTimezone ? (
                <div className="space-y-2">
                  <select
                    value={newTimezone}
                    onChange={(e) => setNewTimezone(e.target.value)}
                    className="input w-full"
                    autoFocus
                  >
                    <option value="">Not set (UTC)</option>
                    {TIMEZONE_NAMES.map((tz) => (
                      <option key={tz} value={tz}>
                        {tz}
                      </option>
                    ))}
                  </select>
                  <div className="flex gap-2">
                    <button
                      onClick={handleUpdateTimezone}
                      className="btn-primary text-sm"
                      disabled={isActionLoading}
                    >
                      Save
                    </button>
                    <button
                      onClick={() => {
                        setNewTimezone(
                          (channel.settings?.timezone as string | undefined) ?? '',
                        )
                        setIsEditingTimezone(false)
                      }}
                      className="btn-secondary text-sm"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-2 flex-wrap">
                  {channel.settings?.timezone ? (
                    <span className="text-text-primary text-sm">
                      {String(channel.settings.timezone)}
                    </span>
                  ) : (
                    /* "Not set" is shown rather than a bare "UTC": a channel
                       that CHOSE UTC and one that was never configured render
                       differently on purpose, here and in the chatter panel. */
                    <span className="text-text-muted text-sm italic">Not set — using UTC</span>
                  )}
                  <button
                    onClick={() => {
                      setNewTimezone(
                        (channel.settings?.timezone as string | undefined) ?? '',
                      )
                      setIsEditingTimezone(true)
                    }}
                    className="text-sm text-text-secondary hover:text-text-primary"
                  >
                    Edit
                  </button>
                </div>
              )}
              <p className="text-text-muted text-xs mt-1.5 leading-relaxed">
                Used to read chatter activity in local hours. Changing it never
                alters stored data.
              </p>
            </div>

            {/* Channel ID */}
            <div>
              <label className="block text-sm text-text-secondary mb-2">Twitch Channel ID</label>
              <code className="text-text-secondary text-sm">{channel.twitch_channel_id}</code>
            </div>
            
            {/* Enabled Modules */}
            <div>
              <label className="block text-sm text-text-secondary mb-2">Enabled Modules</label>
              {channel.enabled_modules.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {channel.enabled_modules.map((mod) => (
                    <span key={mod} className="px-2 py-1 bg-surface-overlay rounded text-sm text-text-secondary">
                      {mod}
                    </span>
                  ))}
                </div>
              ) : (
                <span className="text-text-muted text-sm">No modules enabled</span>
              )}
            </div>
          </div>
        </div>
        
        {/* Chat Panel */}
        <div className="lg:col-span-2 card flex flex-col h-[500px]">
          <h2 className="text-lg font-semibold text-text-primary mb-4 flex items-center gap-2">
            <MessageSquare size={20} />
            Chat
          </h2>
          
          {/* Chat messages */}
          <div
            ref={chatContainerRef}
            className="flex-1 overflow-y-auto bg-surface-app rounded-lg p-3 space-y-1 mb-4"
          >
            {chatMessages.map((msg) => (
              <ChatMessageItem
                key={msg.id}
                msg={msg}
                onSelectChatter={(twitchUserId, name) =>
                  setSelectedChatter({ id: twitchUserId, name })
                }
              />
            ))}
            {chatMessages.length === 0 && (
              <p className="text-text-muted text-center py-8">No messages yet</p>
            )}
          </div>
          
          {/* Message input */}
          <form onSubmit={handleSendMessage} className="flex gap-2">
            <input
              type="text"
              value={messageInput}
              onChange={(e) => setMessageInput(e.target.value)}
              placeholder={
                channel.bot_connected
                  ? `Send a message as ${channel.effective_bot_username || 'the bot'}...`
                  : 'Start the bot to send messages'
              }
              className="input flex-1"
              disabled={!channel.bot_connected || isSending}
            />
            <button
              type="submit"
              className="btn-primary flex items-center gap-2"
              disabled={!channel.bot_connected || !messageInput.trim() || isSending}
            >
              {isSending ? (
                <Loader2 className="animate-spin" size={18} />
              ) : (
                <Send size={18} />
              )}
            </button>
          </form>
          
          {!channel.bot_connected && (
            <p className="text-text-muted text-sm mt-2">
              Start the bot to send messages and see the chat log.
            </p>
          )}
        </div>
      </div>

      {/* Module panels (R25 chat 2): each enabled module that ships a
          ChannelPanel gets one here, below the chat - the page for a
          module's live, bigger actions. */}
      {channel.enabled_modules.map((name) => {
        const Panel = getModule(name)?.ChannelPanel
        return Panel ? (
          <Panel
            key={name}
            channelId={channel.id}
            channelName={channel.twitch_channel_name}
          />
        ) : null
      })}

      {selectedChatter && (
        <ChatterPanel
          channelId={channel.id}
          twitchUserId={selectedChatter.id}
          fallbackName={selectedChatter.name}
          onClose={() => setSelectedChatter(null)}
        />
      )}
    </div>
  )
}
